import React, { useEffect, useState } from "react";
import { API_BASE } from "../config";
import { authHeaders } from "../utils/authHeaders";

const cardStyle = {
  background: "#fff",
  padding: 16,
  borderRadius: 10,
  boxShadow: "0 0 10px rgba(0,0,0,0.08)",
  marginBottom: 16,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle = {
  padding: 10,
  borderBottom: "1px solid #ccc",
  textAlign: "left",
  background: "#f5f5f5",
};

const tdStyle = {
  padding: 10,
  borderBottom: "1px solid #eee",
  verticalAlign: "top",
};

const money = (v) => {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

export default function AgentSettlement() {
  const [activeTab, setActiveTab] = useState("SUMMARY");

  const [summary, setSummary] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [ledger, setLedger] = useState([]);
  const [ledgerAgent, setLedgerAgent] = useState(null);
  const [ledgerFrom, setLedgerFrom] = useState("");
  const [ledgerTo, setLedgerTo] = useState("");
  const [ledgerStatement, setLedgerStatement] = useState({
    opening_balance: 0,
    period_debit: 0,
    period_credit: 0,
    closing_balance: 0,
  });

  const [paymentRequests, setPaymentRequests] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState("PENDING");
  const [error, setError] = useState("");

  const [openingAmount, setOpeningAmount] = useState("");
  const [openingType, setOpeningType] = useState("DEBIT");
  const [openingRemarks, setOpeningRemarks] = useState("");

  const [loading, setLoading] = useState(false);

  const loadSummary = async () => {
    const res = await fetch(`${API_BASE}/agent-settlement/summary`, {
      headers: authHeaders(),
    });
    const data = await res.json();
    if (!res.ok || !data?.success) throw new Error(data?.message || "Could not load summary");
    setSummary(data.data || []);
  };

  const loadLedger = async (agentId, from = ledgerFrom, to = ledgerTo) => {
    if (!agentId) return;

    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);

    const qs = params.toString();
    const url = `${API_BASE}/agent-settlement/ledger/${agentId}${qs ? `?${qs}` : ""}`;

    const res = await fetch(url, {
      headers: authHeaders(),
    });

    const data = await res.json();

    if (data?.success) {
      setLedger(data.data || []);
      setLedgerAgent(data.agent || null);
      setLedgerStatement({
        opening_balance: data.opening_balance || 0,
        period_debit: data.period_debit || 0,
        period_credit: data.period_credit || 0,
        closing_balance: data.closing_balance || 0,
      });
    } else {
      setLedger([]);
      setLedgerAgent(null);
      setLedgerStatement({
        opening_balance: 0,
        period_debit: 0,
        period_credit: 0,
        closing_balance: 0,
      });
      alert(data?.message || "Could not load ledger");
    }
  };

  const loadPaymentRequests = async (status = paymentStatus) => {
    const res = await fetch(`${API_BASE}/agent-payment-requests?status=${encodeURIComponent(status)}`, {
      headers: authHeaders(),
    });
    const data = await res.json();
    if (!res.ok || !data?.success) throw new Error(data?.message || "Could not load payment requests");
    setPaymentRequests(data.data || []);
  };

  const refreshAll = async () => {
    try {
    setError("");
    await loadSummary();
    await loadPaymentRequests();

    if (selectedAgentId) {
      await loadLedger(selectedAgentId);
    }
    } catch (err) { setError(err.message); }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveOpeningBalance = async () => {
    if (!selectedAgentId) {
      alert("Select agent first");
      return;
    }

    const amount = Number(openingAmount);
    if (!amount || Number.isNaN(amount) || amount <= 0) {
      alert("Enter valid opening amount");
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/agent-settlement/opening-balance`, {
        method: "POST",
        headers: authHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          agent_id: Number(selectedAgentId),
          balance_type: openingType,
          amount,
          remarks: openingRemarks || null,
        }),
      });

      const data = await res.json();

      if (!data?.success) {
        alert(data?.message || "Opening balance failed");
        return;
      }

      alert("Opening balance saved");
      setOpeningAmount("");
      setOpeningRemarks("");

      await loadSummary();
      await loadLedger(selectedAgentId);
    } catch (err) {
      console.error(err);
      alert("Server error");
    } finally {
      setLoading(false);
    }
  };

  const acceptPayment = async (request) => {
    const tallyRef = window.prompt(
      `Enter Tally Reference Number for payment of ₹${money(request.amount)} from ${request.agent_name}`
    );

    if (!tallyRef || !tallyRef.trim()) {
      alert("Tally Reference Number is required");
      return;
    }

    const remarks = window.prompt(
      "Admin remarks (optional)",
      "Verified and accepted"
    );

    const ok = window.confirm(
      `Accept payment of ₹${money(request.amount)} from ${request.agent_name}?\n\n` +
        `Mode: ${request.payment_mode}\n` +
        `Payment Ref: ${request.reference_no || "-"}\n` +
        `Tally Ref: ${tallyRef.trim()}`
    );

    if (!ok) return;

    try {
      setLoading(true);

      const res = await fetch(
        `${API_BASE}/agent-payment-requests/${request.id}/accept`,
        {
          method: "POST",
          headers: authHeaders({
            "Content-Type": "application/json",
          }),
          body: JSON.stringify({
            tally_reference_no: tallyRef.trim(),
            remarks: remarks && remarks.trim() ? remarks.trim() : null,
          }),
        }
      );

      const data = await res.json();

      if (!data?.success) {
        alert(data?.message || "Accept payment failed");
        return;
      }

      alert("Payment accepted");
      await refreshAll();
    } catch (err) {
      console.error(err);
      alert("Server error");
    } finally {
      setLoading(false);
    }
  };

  const rejectPayment = async (request) => {
    const reason = window.prompt("Enter rejection reason");
    if (!reason || !reason.trim()) return;

    try {
      setLoading(true);

      const res = await fetch(
        `${API_BASE}/agent-payment-requests/${request.id}/reject`,
        {
          method: "POST",
          headers: authHeaders({
            "Content-Type": "application/json",
          }),
          body: JSON.stringify({
            rejection_reason: reason.trim(),
          }),
        }
      );

      const data = await res.json();

      if (!data?.success) {
        alert(data?.message || "Reject payment failed");
        return;
      }

      alert("Payment rejected");
      await refreshAll();
    } catch (err) {
      console.error(err);
      alert("Server error");
    } finally {
      setLoading(false);
    }
  };

  const selectAgent = async (agentId) => {
    setSelectedAgentId(agentId);
    await loadLedger(agentId);
    setActiveTab("LEDGER");
  };

  const downloadLedgerCsv = () => {
    if (!ledgerAgent) {
      alert("Select agent first");
      return;
    }

    const rows = [];

    rows.push(["Agent", ledgerAgent.name || ""]);
    rows.push(["Phone", ledgerAgent.phone || ""]);
    rows.push(["From", ledgerFrom || "Beginning"]);
    rows.push(["To", ledgerTo || "Today"]);
    rows.push([]);
    rows.push(["Opening Balance", money(ledgerStatement.opening_balance)]);
    rows.push(["Period Debit", money(ledgerStatement.period_debit)]);
    rows.push(["Period Credit", money(ledgerStatement.period_credit)]);
    rows.push(["Closing Balance", money(ledgerStatement.closing_balance)]);
    rows.push([]);
    rows.push([
      "Date",
      "Type",
      "Reference",
      "Debit",
      "Credit",
      "Running Balance",
      "Remarks",
    ]);

    ledger.forEach((r) => {
      rows.push([
        new Date(r.created_at).toLocaleString(),
        r.entry_type || "",
        `${r.ref_type || "-"}${r.ref_id ? ` #${r.ref_id}` : ""}`,
        money(r.debit),
        money(r.credit),
        money(r.running_balance),
        r.remarks || "",
      ]);
    });

    const csv = rows
      .map((row) =>
        row
          .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");

    const blob = new Blob(["\ufeff" + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeAgentName = String(ledgerAgent.name || "agent").replace(/[^a-z0-9]+/gi, "_");
    a.href = url;
    a.download = `agent_ledger_${safeAgentName}_${ledgerFrom || "start"}_to_${ledgerTo || "today"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: 20 }}>
      <h2>Agent Settlement</h2>
      {error && <p role="alert">{error}</p>}

      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        {[
          { key: "SUMMARY", label: "Summary" },
          { key: "LEDGER", label: "Ledger" },
          { key: "OPENING", label: "Opening Balance" },
          { key: "PAYMENTS", label: "Payment Requests" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid #ccc",
              background: activeTab === tab.key ? "#0066ff" : "#fff",
              color: activeTab === tab.key ? "#fff" : "#000",
              cursor: "pointer",
            }}
          >
            {tab.label}
          </button>
        ))}

        <button
          onClick={refreshAll}
          disabled={loading}
          style={{ marginLeft: "auto", padding: "10px 14px" }}
        >
          Refresh
        </button>
      </div>

      {activeTab === "SUMMARY" && (
        <div style={cardStyle}>
          <h3>Agent Balance Summary</h3>

          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Agent</th>
                <th style={thStyle}>Phone</th>
                <th style={thStyle}>Debit</th>
                <th style={thStyle}>Credit</th>
                <th style={thStyle}>Balance</th>
                <th style={thStyle}>Ledger</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((r) => (
                <tr key={r.agent_id}>
                  <td style={tdStyle}>{r.agent_name}</td>
                  <td style={tdStyle}>{r.phone || "-"}</td>
                  <td style={tdStyle}>₹ {money(r.total_debit)}</td>
                  <td style={tdStyle}>₹ {money(r.total_credit)}</td>
                  <td
                    style={{
                      ...tdStyle,
                      fontWeight: 700,
                      color: Number(r.balance || 0) > 0 ? "#b06000" : "#137333",
                    }}
                  >
                    ₹ {money(r.balance)}
                  </td>
                  <td style={tdStyle}>
                    <button onClick={() => selectAgent(r.agent_id)}>
                      View Ledger
                    </button>
                  </td>
                </tr>
              ))}

              {summary.length === 0 && (
                <tr>
                  <td style={tdStyle} colSpan={6}>
                    No agents found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "LEDGER" && (
        <div style={cardStyle}>
          <h3>Agent Ledger</h3>

          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "end",
              marginBottom: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Agent</div>
              <select
                value={selectedAgentId}
                onChange={(e) => selectAgent(e.target.value)}
                style={{ padding: 8, minWidth: 280 }}
              >
                <option value="">Select Agent</option>
                {summary.map((a) => (
                  <option key={a.agent_id} value={a.agent_id}>
                    {a.agent_name} ({a.phone || "-"})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>From</div>
              <input
                type="date"
                value={ledgerFrom}
                onChange={(e) => setLedgerFrom(e.target.value)}
                style={{ padding: 8 }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>To</div>
              <input
                type="date"
                value={ledgerTo}
                onChange={(e) => setLedgerTo(e.target.value)}
                style={{ padding: 8 }}
              />
            </div>

            <button
              onClick={() => loadLedger(selectedAgentId)}
              disabled={!selectedAgentId}
              style={{ padding: "9px 14px" }}
            >
              View Statement
            </button>

            <button
              onClick={downloadLedgerCsv}
              disabled={!ledgerAgent}
              style={{ padding: "9px 14px" }}
            >
              Download CSV
            </button>
          </div>

          {ledgerAgent && (
            <>
              <div style={{ marginBottom: 12, fontWeight: 600 }}>
                Agent: {ledgerAgent.name} | {ledgerAgent.phone || "-"}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(140px, 1fr))",
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <div style={{ background: "#f8f9fa", padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>Opening Balance</div>
                  <div style={{ fontWeight: 800 }}>₹ {money(ledgerStatement.opening_balance)}</div>
                </div>

                <div style={{ background: "#f8f9fa", padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>Period Debit</div>
                  <div style={{ fontWeight: 800 }}>₹ {money(ledgerStatement.period_debit)}</div>
                </div>

                <div style={{ background: "#f8f9fa", padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>Period Credit</div>
                  <div style={{ fontWeight: 800 }}>₹ {money(ledgerStatement.period_credit)}</div>
                </div>

                <div style={{ background: "#f8f9fa", padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>Closing Balance</div>
                  <div
                    style={{
                      fontWeight: 900,
                      color: Number(ledgerStatement.closing_balance || 0) > 0 ? "#b06000" : "#137333",
                    }}
                  >
                    ₹ {money(ledgerStatement.closing_balance)}
                  </div>
                </div>
              </div>
            </>
          )}

          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Reference</th>
                <th style={thStyle}>Debit</th>
                <th style={thStyle}>Credit</th>
                <th style={thStyle}>Balance</th>
                <th style={thStyle}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {ledger.map((r) => (
                <tr key={r.id}>
                  <td style={tdStyle}>{new Date(r.created_at).toLocaleString()}</td>
                  <td style={tdStyle}>{r.entry_type}</td>
                  <td style={tdStyle}>
                    {r.ref_type || "-"} {r.ref_id ? `#${r.ref_id}` : ""}
                  </td>
                  <td style={tdStyle}>₹ {money(r.debit)}</td>
                  <td style={tdStyle}>₹ {money(r.credit)}</td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>
                    ₹ {money(r.running_balance)}
                  </td>
                  <td style={tdStyle}>{r.remarks || "-"}</td>
                </tr>
              ))}

              {ledger.length === 0 && (
                <tr>
                  <td style={tdStyle} colSpan={7}>
                    No ledger entries for selected agent.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "OPENING" && (
        <div style={cardStyle}>
          <h3>Opening Balance</h3>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Agent</div>
              <select
                value={selectedAgentId}
                onChange={(e) => {
                  setSelectedAgentId(e.target.value);
                  if (e.target.value) loadLedger(e.target.value);
                }}
                style={{ padding: 8, width: "100%" }}
              >
                <option value="">Select Agent</option>
                {summary.map((a) => (
                  <option key={a.agent_id} value={a.agent_id}>
                    {a.agent_name} ({a.phone || "-"})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Opening Amount</div>
              <input
                type="number"
                value={openingAmount}
                onChange={(e) => setOpeningAmount(e.target.value)}
                placeholder="Enter positive amount"
                style={{ padding: 8, width: "100%" }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>
                Opening Balance Type
              </div>
              <select
                value={openingType}
                onChange={(e) => setOpeningType(e.target.value)}
                style={{ padding: 8, width: "100%" }}
              >
                <option value="DEBIT">Agent owes FPO - Debit</option>
                <option value="CREDIT">Agent has advance/credit balance - Credit</option>
              </select>
            </div>

            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Remarks</div>
              <input
                value={openingRemarks}
                onChange={(e) => setOpeningRemarks(e.target.value)}
                placeholder="Opening outstanding before settlement module"
                style={{ padding: 8, width: "100%" }}
              />
            </div>
          </div>

          <button
            onClick={saveOpeningBalance}
            disabled={loading}
            style={{
              marginTop: 16,
              padding: "10px 16px",
              background: "#0066ff",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            Save Opening Balance
          </button>

          <div style={{ marginTop: 12, fontSize: 13, opacity: 0.75 }}>
            Note: Opening balance can be saved only once per agent.
          </div>
        </div>
      )}

      {activeTab === "PAYMENTS" && (
        <div style={cardStyle}>
          <h3>Payment Requests</h3>
          <label>Status <select aria-label="Payment status" value={paymentStatus} disabled={loading}
            onChange={async (e) => {
              const status = e.target.value;
              setPaymentStatus(status);
              setPaymentRequests([]);
              try { setError(""); await loadPaymentRequests(status); }
              catch (err) { setError(err.message); }
            }}>
            <option value="PENDING">Pending</option>
            <option value="ACCEPTED">Accepted</option>
            <option value="REJECTED">Rejected</option>
            <option value="">All</option>
          </select></label>

          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Agent</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Mode</th>
                <th style={thStyle}>Reference</th>
                <th style={thStyle}>Received By</th>
                <th style={thStyle}>Remarks</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paymentRequests.map((r) => (
                <tr key={r.id}>
                  <td style={tdStyle}>{new Date(r.requested_at).toLocaleString()}</td>
                  <td style={tdStyle}>
                    {r.agent_name}
                    <br />
                    <span style={{ fontSize: 12, opacity: 0.7 }}>
                      {r.agent_phone || "-"}
                    </span>
                  </td>
                  <td style={tdStyle}>₹ {money(r.amount)}</td>
                  <td style={tdStyle}>{r.payment_mode}</td>
                  <td style={tdStyle}>{r.reference_no || "-"}</td>
                  <td style={tdStyle}>
                    {r.payment_mode === "CASH"
                      ? r.received_by_name || "-"
                      : "Not required"}
                  </td>
                  <td style={tdStyle}>{r.remarks || "-"}
                    <div>{r.status}</div>
                    {r.tally_reference_no && <div>Tally: {r.tally_reference_no}</div>}
                    {r.rejection_reason && <div>Reason: {r.rejection_reason}</div>}
                  </td>
                  <td style={tdStyle}>
                    {r.status === "PENDING" && <>
                    <button
                      onClick={() => acceptPayment(r)}
                      disabled={loading}
                      style={{ marginRight: 8 }}
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => rejectPayment(r)}
                      disabled={loading}
                    >
                      Reject
                    </button>
                    </>}
                  </td>
                </tr>
              ))}

              {paymentRequests.length === 0 && (
                <tr>
                  <td style={tdStyle} colSpan={8}>
                    No payment requests for this status.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div style={{ marginTop: 12, fontSize: 13, opacity: 0.75 }}>
            Only accepted payments reduce the balance. Cash requires an active FPO Authorised Person; all modes require a Tally reference.
          </div>
        </div>
      )}
    </div>
  );
}